import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { type z } from 'zod';
import { Select, type SelectProps } from '@/components/select';
import { ALL_GRADES_NUMBER, isAllGrades } from '@/views/build/util';
import { useSignUpSchoolGrade } from '../hook';
import { type StudentSchema, type TeacherSchema } from '../schema';
import { useGetSchoolTypeOptions } from './selectOptions';
import { WithLabel } from './WithLabel';

/**
 * 회원가입 폼에 들어가는 Select 컴포넌트 모음
 * 학교급 선택, 학년 선택, 이용 목적 선택
 */

// 학교급 선택
export const SignSelectSchoolType = (props: Omit<SelectProps, 'options'>) => {
  const {
    value,
    onValueChange,
    ...restProps
  } = props;
  const schoolTypeOptions = useGetSchoolTypeOptions();
  return <Select value={value} onValueChange={onValueChange} options={schoolTypeOptions} {...restProps} className="h-[47px] w-full rounded-sm border-[1px]" data-sentry-element="Select" data-sentry-component="SignSelectSchoolType" data-sentry-source-file="SignSelect.tsx" />;
};

// 학년 선택
export const SignSelectSchoolGrade = (props: Omit<SelectProps, 'options'>) => {
  const {
    onValueChange,
    ...restProps
  } = props;
  const {
    control
  } = useFormContext<z.infer<typeof TeacherSchema | typeof StudentSchema>>();
  const schoolType = useWatch({
    control,
    name: 'schoolType'
  });
  const value = useWatch({
    control,
    name: 'schoolGrade'
  });
  const userType = useWatch({
    control,
    name: 'userType'
  });
  const {
    t
  } = useTranslation(['common']);
  const grades = useSignUpSchoolGrade();
  const noGrade = schoolType === 'COLLEGE' || schoolType === 'OTHER' || schoolType === 'KINDERGARTEN';

  /**
   * 전학년(99)은 교사에게만 노출한다.
   * 학생의 학년은 "내가 몇 학년인가"라 전학년이 성립하지 않지만(#2152 에서 제외),
   * 교사의 학년은 "내가 가르치는 학년"이라 전학년이 유효하다.
   * userType 은 가입(router.query.type)·프로필 수정(profile.type) 양쪽 모두 소문자로 채워진다.
   */
  const allowsAllGrades = userType === 'teacher';
  const gradeOptions = useMemo(() => [...grades.filter(grade => allowsAllGrades || grade !== ALL_GRADES_NUMBER).map(grade => ({
    label: isAllGrades(grade) ? t('common:school.info.allGrades') : t('common:school.info.nGrade', {
      n: grade.toString()
    }),
    value: grade.toString()
  })), ...(noGrade ? [{
    label: t('signup.school.noGrade'),
    value: '0'
  }] : [])], [grades, noGrade, t, allowsAllGrades]);
  return <Select placeHolder={t('signup.school.gradePlaceholder')} value={value} defaultValue={noGrade ? '0' : ''} onValueChange={onValueChange} disabled={noGrade} options={gradeOptions} {...restProps} className="h-[47px] w-full rounded-sm border-[1px]" data-sentry-element="Select" data-sentry-component="SignSelectSchoolGrade" data-sentry-source-file="SignSelect.tsx" />;
};

// 이용 목적 선택
export const SignSelectUsagePurpose = (props: SelectProps) => {
  const {
    t
  } = useTranslation(['common']);
  const errorMessage = props.isError ? t('account.signup.requiredField') : undefined;
  return <WithLabel label={t('account.signup.purposeUse')} errorMessage={errorMessage} data-sentry-element="WithLabel" data-sentry-component="SignSelectUsagePurpose" data-sentry-source-file="SignSelect.tsx">
      <Select {...props} placeHolder={t('account.signup.selectPurposeUse')} isError={!!errorMessage} options={props.options} className={`border-input-border ${props.isError ? 'border-[#FFACB6] focus-within:border-[#FFACB6]' : ''}`} data-sentry-element="Select" data-sentry-source-file="SignSelect.tsx" />
    </WithLabel>;
};